import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Instagram, Linkedin, Mail, MessageCircle, Youtube } from "lucide-react";
import { useLanding } from "@/context/LandingContext";
import { BRAND, LEGAL } from "@/config/site";
import { SEMINAR } from "@/seminars";
import { Brand } from "./Brand";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, youtube: Youtube, tiktok: TikTokIcon };

function scrollToHash(href) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function whatsappHref(message) {
  return `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(message)}`;
}

export function Footer() {
  const { t } = useLanding();
  const f = t.footer;
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand-col">
            <Brand testId="footer-brand" />
            <p className="footer-blurb" data-testid="footer-blurb">{f.blurb}</p>
            <p className="footer-col-title">{f.connect}</p>
            <div className="footer-social" data-testid="footer-social">
              {BRAND.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.icon];
                return (
                  <a key={s.icon} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label} className="footer-social-link" data-testid={`footer-social-${s.icon}`}>
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
            <div className="footer-contact">
              <a href={`mailto:${BRAND.contactEmail}`} data-testid="footer-email-link"><Mail size={14} /> {BRAND.contactEmail}</a>
              <a href={whatsappHref(f.whatsappMessage)} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"><MessageCircle size={14} /> {BRAND.whatsappDisplay}</a>
            </div>
          </div>
          <div className="footer-quicklinks" data-testid="footer-quicklinks">
            <p className="footer-col-title">{f.quickLinksTitle}</p>
            <ul>
              {f.quickLinks.map((link, i) => (
                <li key={link.href}>
                  <button type="button" onClick={() => scrollToHash(link.href)} data-testid={`footer-quicklink-${i}`}>
                    <span>{link.label}</span>
                    <ArrowRight size={15} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="footer-divider" />
        <div className="footer-links">
          <Link to="/privacy-policy" data-testid="footer-privacy-link">{f.links[0]}</Link>
          <Link to="/terms-of-use" data-testid="footer-terms-link">{f.links[1]}</Link>
          <a href="#faq" data-testid="footer-faq-link">{f.links[2]}</a>
        </div>
        <p className="footer-mas" data-testid="footer-mas-statement">{LEGAL.masStatement}</p>
        <div className="footer-disclaimers" data-testid="footer-disclaimers">
          <p>{f.disclaimer}</p>
          {LEGAL.disclaimers.map((text) => (
            <p key={text.slice(0, 48)}>{text}</p>
          ))}
        </div>
        <p className="footer-copy" data-testid="footer-copyright">
          © {new Date().getFullYear()} {f.rights} {BRAND.tagline}.
        </p>
      </div>
    </footer>
  );
}

export function FloatingWhatsApp() {
  const { t } = useLanding();
  return (
    <a href={whatsappHref(t.footer.whatsappMessage)} target="_blank" rel="noopener noreferrer" aria-label="Enquire on WhatsApp" className="floating-whatsapp" data-testid="floating-whatsapp-button">
      <span className="floating-whatsapp-pulse" aria-hidden="true" />
      <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
}

export function StickyCtas() {
  const { t, lang, selectedEvent, sessionTag, setRegisterOpen } = useLanding();
  const [visible, setVisible] = useState(false);
  const closed = SEMINAR.registrationClosed;

  useEffect(() => {
    const update = () => {
      const register = document.getElementById("register");
      if (!register) return;
      const top = register.getBoundingClientRect().top;
      setVisible(window.scrollY > window.innerHeight * 0.72 && top > window.innerHeight * 0.72);
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);

  return (
    <>
      <div className={`mobile-cta ${visible ? "visible" : ""}`} data-testid="mobile-cta">
        <span className="mobile-cta-copy">
          <strong>{closed ? t.closed.badge : t.sticky.mobileTitle}</strong>
          <small data-testid="mobile-cta-session">{closed ? t.closed.title : `${selectedEvent.short[lang]} · ${sessionTag(selectedEvent)}`}</small>
        </span>
        <button type="button" className="btn btn-gold" onClick={() => setRegisterOpen(true)} data-testid="mobile-cta-reserve">{closed ? t.closed.cta : t.sticky.reserve}</button>
      </div>
      <aside className={`desktop-cta ${visible ? "visible" : ""}`} aria-label="Webinar registration reminder" data-testid="desktop-cta">
        <small data-testid="desktop-cta-kicker">{closed ? t.closed.badge : sessionTag(selectedEvent)}</small>
        <strong>{closed ? t.closed.title : selectedEvent.label[lang]}</strong>
        <button type="button" className="btn btn-gold" onClick={() => setRegisterOpen(true)} data-testid="desktop-cta-reserve">{closed ? t.closed.cta : t.sticky.reserveLong}</button>
      </aside>
    </>
  );
}
